export type OrgTab = {
  id: string;
  label: string;
  className?: string;
};

export type TabFilterConditions = {
  isEnterpriseBillingOrThirdParty: boolean;
  isCpqAddOnTrialSelfServe: boolean;
  isPlanEnterprise: boolean;
  isCpqChannel: boolean;
  isAppStoreApple: boolean;
  /** Controls plans tab visibility for APPSTORE_APPLE; billing is always hidden regardless. */
  isAppleSelfServeEnabled: boolean;
  isFreePlan: boolean;
  isResellerXero: boolean;
  isEmbedDrb: boolean;
};

/**
 * Per-tab visibility rules. Each rule returns whether the tab is visible
 * given the current conditions. Rules include global constraints as needed.
 */
export const TAB_VISIBILITY_RULES: Record<
  string,
  (ctx: TabFilterConditions) => boolean
> = {
  'edit-org-general': (_ctx: TabFilterConditions) => {
    return true;
  },
  'edit-org-billing': (ctx: TabFilterConditions) => {
    if (
      ctx.isEnterpriseBillingOrThirdParty ||
      ctx.isFreePlan ||
      ctx.isResellerXero ||
      ctx.isEmbedDrb ||
      ctx.isAppStoreApple
    ) {
      return false;
    }
    return true;
  },
  'edit-org-plans': (ctx: TabFilterConditions) => {
    if (
      ctx.isEnterpriseBillingOrThirdParty ||
      (ctx.isAppStoreApple && !ctx.isAppleSelfServeEnabled) ||
      (!ctx.isCpqAddOnTrialSelfServe &&
        (ctx.isPlanEnterprise || ctx.isCpqChannel)) ||
      (ctx.isPlanEnterprise && !ctx.isCpqChannel)
    ) {
      return false;
    }
    return true;
  },
  'edit-org-statement': (ctx: TabFilterConditions) => {
    if (
      ctx.isEnterpriseBillingOrThirdParty ||
      ctx.isResellerXero ||
      ctx.isEmbedDrb ||
      ctx.isAppStoreApple
    ) {
      return false;
    }
    return true;
  },
};

/**
 * Returns whether an organization tab should be visible. Falls back to a safe
 * default for unknown tabs while preserving the global enterprise/third-party rule.
 */
export function isOrganizationTabVisible(
  tabId: string,
  ctx: TabFilterConditions
): boolean {
  const rule = TAB_VISIBILITY_RULES[tabId];
  if (rule) {
    return rule(ctx);
  }
  // Default behavior for unknown tabs:
  if (ctx.isEnterpriseBillingOrThirdParty) {
    return tabId === 'edit-org-general';
  }
  return true;
}

/**
 * Applies consistent visibility rules to the Organization tabs array.
 *
 * The logic mirrors the rules used in the Once app for determining which tabs
 * should be visible based on plan, channel and feature flag conditions.
 */
export function filterOrganizationTabs(
  allTabs: OrgTab[],
  {
    isEnterpriseBillingOrThirdParty,
    isCpqAddOnTrialSelfServe,
    isPlanEnterprise,
    isCpqChannel,
    isAppStoreApple,
    isAppleSelfServeEnabled,
    isFreePlan,
    isResellerXero,
    isEmbedDrb,
  }: TabFilterConditions
): OrgTab[] {
  const ctx: TabFilterConditions = {
    isEnterpriseBillingOrThirdParty,
    isCpqAddOnTrialSelfServe,
    isPlanEnterprise,
    isCpqChannel,
    isAppStoreApple,
    isAppleSelfServeEnabled,
    isFreePlan,
    isResellerXero,
    isEmbedDrb,
  };
  return allTabs.filter(tab => isOrganizationTabVisible(tab.id, ctx));
}
